class WFaqComponent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.onTabClick = this.onTabClick.bind(this);
    this.onAccordionToggle = this.onAccordionToggle.bind(this);
    this.onAccordionClick = this.onAccordionClick.bind(this);
    this.onPackChange = this.onPackChange.bind(this);
    this.onPackButtonClick = this.onPackButtonClick.bind(this);

    this.initTabs();
    this.initAccordions();
    this.initPackSync();
  }

  disconnectedCallback() {
    if (this.tabButtons && this.tabButtons.length) {
      this.tabButtons.forEach((button) => {
        button.removeEventListener('click', this.onTabClick);
      });
    }

    if (this.accordions && this.accordions.length) {
      this.accordions.forEach((accordion) => {
        accordion.removeEventListener('toggle', this.onAccordionToggle);
        const summary = accordion.querySelector('.w-faq__accordion-summary');
        if (summary) {
          summary.removeEventListener('click', this.onAccordionClick);
        }
      });
    }
    document.removeEventListener('main-product:pack-change', this.onPackChange);
    document.removeEventListener('click', this.onPackButtonClick);

    this.initialized = false;
  }

  initTabs() {
    this.tabsContainer = this.querySelector('[data-faq-tabs]');
    this.tabButtons = Array.from(this.querySelectorAll('[data-faq-tab-trigger]'));
    this.tabPanels = Array.from(this.querySelectorAll('[data-faq-tab-panel]'));
    this.activeTab = '';

    if (!this.tabsContainer || !this.tabButtons.length || !this.tabPanels.length) return;

    this.tabsContainer.style.setProperty('--faq-tab-count', `${this.tabButtons.length}`);

    let defaultTab = this.tabButtons[0].dataset.faqTabTrigger || '';

    this.tabButtons.forEach((button, index) => {
      button.dataset.faqTabIndex = `${index}`;
      if (button.classList.contains('is-active') && button.dataset.faqTabTrigger) {
        defaultTab = button.dataset.faqTabTrigger;
      }
      button.addEventListener('click', this.onTabClick);
    });

    if (defaultTab) {
      this.setActiveTab(defaultTab);
    }
  }

  onTabClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    const tabId = button.dataset.faqTabTrigger || '';
    if (!tabId || tabId === this.activeTab) return;
    this.setActiveTab(tabId);
  }

  setActiveTab(tabId) {
    this.activeTab = tabId;

    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.faqTabTrigger === tabId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive && this.tabsContainer) {
        this.tabsContainer.style.setProperty('--faq-tab-index', button.dataset.faqTabIndex || '0');
      }
    });

    this.tabPanels.forEach((panel) => {
      const isActive = panel.dataset.faqTabPanel === tabId;
      panel.hidden = !isActive;
      if (!isActive) return;
      this.syncPanelAccordionHeights(panel);
    });
  }

  initAccordions() {
    this.accordions = Array.from(this.querySelectorAll('.w-faq__accordion'));
    if (!this.accordions.length) return;

    this.accordions.forEach((accordion) => {
      const content = accordion.querySelector('.w-faq__accordion-content');
      const summary = accordion.querySelector('.w-faq__accordion-summary');
      if (!content) return;
      const isBound = accordion.dataset.faqAccordionBound === 'true';
      if (!isBound) {
        accordion.addEventListener('toggle', this.onAccordionToggle);
        if (summary) {
          summary.addEventListener('click', this.onAccordionClick);
        }
        accordion.dataset.faqAccordionBound = 'true';
      }
      if (accordion.hasAttribute('open')) {
        content.style.height = 'auto';
      } else {
        content.style.height = '0px';
      }
    });
  }

  onAccordionToggle(event) {
    const accordion = event.target;
    if (!(accordion instanceof HTMLElement)) return;
    const content = accordion.querySelector('.w-faq__accordion-content');
    if (!content) return;

    if (accordion.open) {
      content.style.height = `${content.scrollHeight}px`;
    } else {
      content.style.height = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        content.style.height = '0px';
      });
    }
  }

  onAccordionClick(event) {
    event.preventDefault();
    const summary = event.currentTarget;
    if (!(summary instanceof HTMLElement)) return;

    const accordion = summary.closest('.w-faq__accordion');
    if (!accordion) return;

    const content = accordion.querySelector('.w-faq__accordion-content');
    if (!content) return;

    const parentList = accordion.closest('.w-faq__accordion-list');
    const siblings = parentList
      ? Array.from(parentList.querySelectorAll('.w-faq__accordion'))
      : this.accordions;

    if (accordion.open) {
      content.style.height = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        content.style.height = '0px';
      });
      content.addEventListener(
        'transitionend',
        () => {
          accordion.open = false;
        },
        { once: true }
      );
      return;
    }

    siblings.forEach((item) => {
      if (item === accordion || !item.open) return;
      const itemContent = item.querySelector('.w-faq__accordion-content');
      if (!itemContent) return;
      itemContent.style.height = `${itemContent.scrollHeight}px`;
      requestAnimationFrame(() => {
        itemContent.style.height = '0px';
      });
      itemContent.addEventListener(
        'transitionend',
        () => {
          item.open = false;
        },
        { once: true }
      );
    });

    accordion.open = true;
    const targetHeight = content.scrollHeight;
    content.style.height = '0px';
    requestAnimationFrame(() => {
      content.style.height = `${targetHeight}px`;
    });
    content.addEventListener(
      'transitionend',
      () => {
        content.style.height = 'auto';
      },
      { once: true }
    );
  }

  syncPanelAccordionHeights(panel) {
    const panelAccordions = panel.querySelectorAll('.w-faq__accordion');
    panelAccordions.forEach((accordion) => {
      const content = accordion.querySelector('.w-faq__accordion-content');
      if (!content) return;
      content.style.height = accordion.open ? 'auto' : '0px';
    });
  }

  initPackSync() {
    this.packSyncEnabled = this.dataset.faqPackSyncEnabled === 'true';
    if (!this.packSyncEnabled) return;

    this.productPanel = this.querySelector('[data-faq-product-panel]');
    this.packTemplates = Array.from(this.querySelectorAll('[data-faq-pack-template]'));
    this.packDataByProductId = this.getMainPackDataMap();
    this.emptyProductText = this.productPanel?.querySelector('.w-faq__empty')?.textContent?.trim() || '';
    this.openFirstItemDefault = Boolean(this.productPanel?.querySelector('.w-faq__accordion[open]'));
    if (!this.productPanel) return;

    document.addEventListener('main-product:pack-change', this.onPackChange);
    document.addEventListener('click', this.onPackButtonClick);

    const initialProductId = this.getActivePackProductId();
    if (initialProductId) {
      if (!this.applyPackFaqFromData(initialProductId)) {
        this.applyPackFaq(initialProductId);
      }
    }
  }

  getActivePackProductId() {
    const activeButton = document.querySelector('[data-pack-form] [data-pack-quantity-button].is-active');
    if (activeButton) return String(activeButton.dataset.packProductId || '');

    const firstButton = document.querySelector('[data-pack-form] [data-pack-quantity-button]:not([hidden]):not([disabled])');
    return String(firstButton?.dataset.packProductId || '');
  }

  onPackChange(event) {
    const productId = String(event?.detail?.productId || '');
    if (!productId) return;
    const faqQuestions = Array.isArray(event?.detail?.faqQuestions) ? event.detail.faqQuestions : null;
    const faqAnswers = Array.isArray(event?.detail?.faqAnswers) ? event.detail.faqAnswers : null;
    if (faqQuestions) {
      this.applyPackFaqData(productId, faqQuestions, faqAnswers || []);
      return;
    }
    this.applyPackFaq(productId);
  }

  onPackButtonClick(event) {
    const button = event.target.closest('[data-pack-form] [data-pack-quantity-button]');
    if (!button) return;

    requestAnimationFrame(() => {
      const productId = this.getActivePackProductId() || String(button.dataset.packProductId || '');
      if (!productId) return;
      this.applyPackFaq(productId);
    });
  }

  applyPackFaq(productId) {
    if (!this.productPanel || !productId) return;
    if (this.currentPackProductId === productId) return;

    const template = this.packTemplates.find((item) => String(item.dataset.packProductId || '') === productId);
    if (!template) {
      this.applyPackFaqFromData(productId);
      return;
    }

    this.productPanel.innerHTML = template.innerHTML;
    this.currentPackProductId = productId;
    this.initAccordions();
    this.syncPanelAccordionHeights(this.productPanel);
  }

  getMainPackDataMap() {
    const map = new Map();
    const jsonEl = document.querySelector('[data-pack-form] [data-pack-products-json]');
    if (!jsonEl) return map;

    try {
      const data = JSON.parse(jsonEl.textContent || '{}');
      const packs = Array.isArray(data?.packs) ? data.packs : [];
      packs.forEach((pack) => {
        const id = String(pack?.productId || '');
        if (!id) return;
        map.set(id, pack);
      });
    } catch (error) {
      return map;
    }

    return map;
  }

  applyPackFaqFromData(productId) {
    const pack = this.packDataByProductId?.get(String(productId));
    if (!pack) return false;
    const questions = Array.isArray(pack.faqQuestions) ? pack.faqQuestions : [];
    const answers = Array.isArray(pack.faqAnswers) ? pack.faqAnswers : [];
    this.applyPackFaqData(String(productId), questions, answers);
    return true;
  }

  applyPackFaqData(productId, questions, answers) {
    if (!this.productPanel || !productId) return;
    if (this.currentPackProductId === productId) return;

    this.productPanel.innerHTML = this.buildPackFaqMarkup(questions, answers);
    this.currentPackProductId = productId;
    this.initAccordions();
    this.syncPanelAccordionHeights(this.productPanel);
  }

  buildPackFaqMarkup(questions, answers) {
    const safeQuestions = Array.isArray(questions)
      ? questions.filter((item) => typeof item === 'string' && item.trim() !== '')
      : [];
    const safeAnswers = Array.isArray(answers) ? answers : [];
    const openFirst = this.openFirstItemDefault;

    if (!safeQuestions.length) {
      return `<p class="w-faq__empty">${this.escapeFaqHtml(this.emptyProductText)}</p>`;
    }

    const itemsHtml = safeQuestions.map((question, index) => {
      const answer = safeAnswers[index] || '';
      const answerHtml = this.formatFaqAnswer(answer);
      return `
        <details class="w-faq__accordion"${openFirst && index === 0 ? ' open' : ''}>
          <summary class="w-faq__accordion-summary">
            <span class="w-faq__accordion-title">${this.escapeFaqHtml(question)}</span>
            <span class="w-faq__accordion-icon w-faq__accordion-icon--closed">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4.16663 10H15.8333" stroke="#2C2B94" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 4.1665V15.8332" stroke="#2C2B94" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="w-faq__accordion-icon w-faq__accordion-icon--open">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="2" viewBox="0 0 14 2" fill="none">
                <path d="M13.8008 1.98242H0V0H13.8008V1.98242Z" fill="#2C2B94"/>
              </svg>
            </span>
          </summary>
          <div class="w-faq__accordion-content">${answerHtml}</div>
        </details>
      `;
    }).join('');

    return `<div class="w-faq__accordion-list">${itemsHtml}</div>`;
  }

  formatFaqAnswer(answer) {
    const escaped = this.escapeFaqHtml(String(answer || ''));
    return escaped
      .replace(/\/br/g, '<br><br>')
      .replace(/\/n/g, '<br>')
      .replace(/\n/g, '<br>');
  }

  escapeFaqHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('w-faq-component')) {
  customElements.define('w-faq-component', WFaqComponent);
}
